import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatDate, formatEUR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DocumentPreviewDrawer from "@/components/DocumentPreviewDrawer";
import { MailboxScanPicker } from "@/components/messaging/MailboxScanPicker";
import { EnvelopeSimple, FileText, Phone, ArrowDownLeft, ArrowUpRight, UploadSimple, Eye, Note } from "@phosphor-icons/react";

const KIND = { email: ["E-Mail", EnvelopeSimple], letter: ["Brief", FileText], call: ["Telefonat", Phone], note: ["Notiz", Note] };
const RI_STATUS = { draft: "Entwurf", pending_approval: "Zur Freigabe", approved: "Freigegeben", sent: "Versendet – Zustimmung offen",
  consent_received: "Zustimmung eingegangen", agreed: "Zugestimmt", partially_agreed: "Teilweise zugestimmt", rejected: "Abgelehnt",
  applied: "Wirksam", cancelled: "Storniert" };

function Fact({ label, children, testId }) {
  return <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div><div className="text-sm font-medium text-slate-900 truncate" data-testid={testId}>{children || "—"}</div></div>;
}

export function AkteBezuege({ akte }) {
  const t = akte.tenancy;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-md border border-slate-200 bg-white p-3" data-testid="akte-bezuege">
      <Fact label="Mietverhältnis" testId="akte-tenancy">{t ? <Link className="text-teal-700 hover:underline" to={`/mietverhaeltnisse/${t.id}${akte.rent_increase ? "?tab=mieterhoehung" : ""}`}>{t.contract_number}</Link> : null}</Fact>
      <Fact label="Mieter" testId="akte-tenants">{t?.tenant_names}</Fact>
      <Fact label="Objekt" testId="akte-property">{akte.property ? <Link className="text-teal-700 hover:underline" to={`/objekte/${akte.property.id}`}>{akte.property.name}</Link> : null}</Fact>
      <Fact label="Bearbeiter / Objektbetreuer" testId="akte-assignee">{akte.assignee_name}</Fact>
    </div>
  );
}

export function RentIncreaseCard({ r }) {
  if (!r) return null;
  const sh = r.shipment;
  return (
    <div className="rounded-md border border-teal-200 bg-teal-50/50 p-3 space-y-2" data-testid="akte-rent-increase">
      <div className="flex items-center justify-between"><div className="text-xs font-semibold uppercase tracking-wider text-teal-900">Mieterhöhung § 558 BGB</div>
        <span className="text-xs rounded-full bg-white border border-teal-200 px-2 py-0.5" data-testid="akte-ri-status">{RI_STATUS[r.status] || r.status}</span></div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Fact label="Nettokaltmiete">{`${formatEUR(r.current_rent)} → ${formatEUR(r.agreed_rent || r.new_rent)}`}</Fact>
        <Fact label="Schreiben vom">{formatDate(r.letter_date)}</Fact>
        <Fact label="Zustimmung bis">{formatDate(r.consent_deadline)}</Fact>
        <Fact label="Wirksam ab">{formatDate(r.effective_date)}</Fact>
        <Fact label="Versand">{r.dispatch ? `${formatDate(r.dispatch.sent_date)}${!r.dispatch.delivered_assumed && r.dispatch.delivered_date ? ` · Zugang ${formatDate(r.dispatch.delivered_date)}` : ""}${r.shipment?.tracking_number ? ` · Sendungsnr. ${r.shipment.tracking_number}` : ""}` : "noch nicht dokumentiert"}</Fact>
        <Fact label="Sendung">{sh?.tracking_number ? <a className="text-teal-700 hover:underline font-mono" href={sh.url} target="_blank" rel="noreferrer">{sh.tracking_number}</a> : null}</Fact>
        <Fact label="Klagefrist">{formatDate(r.klage_deadline)}</Fact>
      </div>
    </div>
  );
}

export function Correspondence({ items, onOpen }) {
  if (!items.length) return <div className="text-sm text-slate-500 py-6 text-center" data-testid="akte-comms-empty">Noch kein Schriftverkehr.</div>;
  return (
    <div className="space-y-1.5" data-testid="akte-comms">
      {items.map((c) => {
        const [lbl, Icon] = KIND[c.kind] || [c.kind, FileText];
        const Dir = c.direction === "in" ? ArrowDownLeft : ArrowUpRight;
        return (
          <div key={c.id} className="flex items-start gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 hover:border-slate-300" data-testid={`akte-comm-${c.id}`}>
            <Icon size={18} className="text-slate-500 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900"><Dir size={12} className={c.direction === "in" ? "text-indigo-600" : "text-emerald-600"} /><span className="truncate">{c.subject || lbl}</span></div>
              <div className="text-[11px] text-slate-500">{lbl} · {c.direction === "in" ? "Eingang" : "Ausgang"} · {formatDate(c.occurred_at)}{c.author_name ? ` · ${c.author_name}` : ""}</div>
              {c.content_text && <div className="text-xs text-slate-600 line-clamp-2 mt-0.5">{c.content_text}</div>}
            </div>
            {c.document_id && <Button size="sm" variant="outline" className="h-7 gap-1 shrink-0" onClick={() => onOpen(c.document_id)} data-testid={`akte-comm-open-${c.id}`}><Eye size={13} /> Öffnen</Button>}
          </div>
        );
      })}
    </div>
  );
}

function DocRow({ d, onOpen, child }) {
  return (
    <button type="button" onClick={() => onOpen(d.id)} className={`w-full text-left flex items-center gap-3 rounded-md border bg-white px-3 py-2 hover:bg-slate-50 ${child ? "border-dashed border-slate-200 ml-6 w-[calc(100%-1.5rem)]" : "border-slate-200"}`} data-testid={`akte-doc-${d.id}`}>
      <FileText size={child ? 15 : 18} className={child ? "text-slate-400 shrink-0" : "text-rose-600 shrink-0"} />
      <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{child && <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1.5">Anlage</span>}{d.title || d.original_filename}</div>
        <div className="text-[11px] text-slate-500">{d.category || "Dokument"} · {formatDate(d.created_at)}{d.signature_status === "pending_scan" ? " · Scan ausstehend" : ""}</div></div>
      <Eye size={15} className="text-slate-400" />
    </button>
  );
}

// Schreiben mit ihren Anlagen (Rückantwort, Versandbeleg …) gruppiert anzeigen
function DocTree({ docs, onOpen }) {
  const byId = Object.fromEntries(docs.map((d) => [d.id, d]));
  const childOf = {};
  docs.forEach((d) => (d.related_document_ids || []).forEach((c) => { if (byId[c] && c !== d.id) childOf[c] = d.id; }));
  docs.forEach((d) => { if (d.parent_document_id && byId[d.parent_document_id] && !childOf[d.id]) childOf[d.id] = d.parent_document_id; });
  const roots = docs.filter((d) => !childOf[d.id]);
  return roots.map((d) => (
    <div key={d.id} className="space-y-1">
      <DocRow d={d} onOpen={onOpen} />
      {docs.filter((c) => childOf[c.id] === d.id).map((c) => <DocRow key={c.id} d={c} onOpen={onOpen} child />)}
    </div>
  ));
}

export function Documents({ vorgangId, docs, onOpen, onChanged }) {
  const ref = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/vorgaenge/${vorgangId}/documents/upload`, fd); toast.success("Dokument zur Akte hinzugefügt"); onChanged(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Upload fehlgeschlagen"); }
  };
  return (
    <div className="space-y-2" data-testid="akte-docs">
      <div className="flex flex-wrap gap-2">
        <input ref={ref} type="file" className="hidden" onChange={upload} data-testid="akte-upload-input" />
        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => ref.current?.click()} data-testid="akte-upload-btn"><UploadSimple size={14} /> Dokument hochladen</Button>
        <MailboxScanPicker allKinds endpoint={(mid) => `/vorgaenge/${vorgangId}/documents/from-mailbox/${mid}`} onPicked={onChanged}
          title="Aus dem Postfach zum Vorgang hinzufügen" label="Aus Postfach hinzufügen" testId="akte-mailbox" />
      </div>
      {!docs.length && <div className="text-sm text-slate-500 py-6 text-center">Noch keine Dokumente.</div>}
      <DocTree docs={docs} onOpen={onOpen} />
    </div>
  );
}

export default function VorgangAkte({ akte, activityTab, onChanged }) {
  const [doc, setDoc] = useState(null);
  return (
    <div className="space-y-3">
      <AkteBezuege akte={akte} />
      <RentIncreaseCard r={akte.rent_increase} />
      <Tabs defaultValue="comms">
        <TabsList>
          <TabsTrigger value="comms" data-testid="vorgang-tab-comms">Schriftverkehr ({akte.communications.length})</TabsTrigger>
          <TabsTrigger value="docs" data-testid="vorgang-tab-docs">Dokumente ({akte.documents.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="vorgang-tab-timeline">Verlauf & Notizen</TabsTrigger>
        </TabsList>
        <TabsContent value="comms" className="pt-2"><Correspondence items={akte.communications} onOpen={setDoc} /></TabsContent>
        <TabsContent value="docs" className="pt-2"><Documents vorgangId={akte.vorgang.id} docs={akte.documents} onOpen={setDoc} onChanged={onChanged} /></TabsContent>
        <TabsContent value="timeline" className="pt-2">{activityTab}</TabsContent>
      </Tabs>
      {doc && <DocumentPreviewDrawer docId={doc} onClose={() => setDoc(null)} />}
    </div>
  );
}
